/**
 * Top-right killfeed. Canvas rather than DOM so the name text, the weapon glyph
 * and the dark halo all come out of the same rasteriser — DOM text plus an
 * inline canvas glyph never quite line up on the baseline.
 *
 * Entries slide in from the right with an ease-out-quint, hold, then fade.
 */
import { COL, rgba, clamp01, ease, fitCanvas, clearCanvas, devicePx } from './Theme.js';
import { label, measureLabel, icon, WEAPON_ICON, ICON } from './Glyphs.js';

export const W = 460, H = 190;
const ROW = 30;
const IN_DUR = 0.26;
const HOLD = 5.0;
const OUT_DUR = 0.55;
export const LIFE = IN_DUR + HOLD + OUT_DUR;

export class Killfeed {
  constructor(root) {
    this.cv = document.createElement('canvas');
    this.cv.className = 'hud-c hud-killfeed';
    root.appendChild(this.cv);
    this.g = fitCanvas(this.cv, W, H);
    this.rows = [];
    this.dirty = true;
  }

  resize(dpr = devicePx()) {
    this.g = fitCanvas(this.cv, W, H, dpr);
    this.dirty = true;
  }

  /** e: { attacker, victim, weapon, headshot, mine, attackerFriendly, victimFriendly } */
  push(e) {
    this.rows.push({ t: 0, ...e });
    while (this.rows.length > 5) this.rows.shift();
    this.dirty = true;
  }

  advance(dt) {
    if (!this.rows.length) return;
    const n0 = this.rows.length;
    for (let i = this.rows.length - 1; i >= 0; i--) {
      this.rows[i].t += dt;
      if (this.rows[i].t > LIFE) this.rows.splice(i, 1);
    }
    // Rows in their hold phase are visually static — only slide-in, fade-out or
    // a row leaving the stack needs a redraw.
    this.dirty = this.rows.length !== n0
      || this.rows.some((r) => r.t < IN_DUR || r.t > IN_DUR + HOLD);
  }

  draw() {
    const g = this.g;
    clearCanvas(g);
    // Oldest at the top, newest sliding in at the bottom of the stack.
    for (let i = 0; i < this.rows.length; i++) drawRow(g, this.rows[i], i * ROW);
    this.dirty = false;
  }
}

function drawRow(g, r, y) {
  const inP = ease.outQuint(clamp01(r.t / IN_DUR));
  const outP = clamp01((r.t - IN_DUR - HOLD) / OUT_DUR);
  const alpha = (1 - ease.outCubic(outP)) * (0.25 + 0.75 * inP);
  if (alpha <= 0.01) return;

  const slide = (1 - inP) * 54;
  const nameOpt = { size: 13, weight: 700, tracking: 0.16, baseline: 'middle' };
  const aCol = r.attackerFriendly === false ? COL.enemy : r.mine ? COL.amber : COL.ally;
  const vCol = r.victimFriendly ? COL.ally : COL.enemy;

  const attacker = (r.attacker || 'UNKNOWN').toUpperCase();
  const victim = (r.victim || 'UNKNOWN').toUpperCase();
  const aw = measureLabel(g, attacker, nameOpt);
  const vw = measureLabel(g, victim, nameOpt);
  const iconW = 34;
  const skullW = r.headshot ? 18 : 0;
  const pad = 9;
  const total = aw + 10 + iconW + skullW + 10 + vw + pad * 2;

  const right = W - 2 + slide;
  const x0 = right - total;
  const cy = y + ROW / 2;

  g.save();
  g.globalAlpha = alpha;

  // No hard plate — COD's killfeed is text on a soft wash. A gradient that
  // fades out to the left keeps it legible without turning into a button.
  const wash = g.createLinearGradient(x0, 0, right, 0);
  wash.addColorStop(0, 'rgba(4,7,10,0)');
  wash.addColorStop(0.22, r.mine ? 'rgba(28,20,4,0.52)' : 'rgba(4,7,10,0.40)');
  wash.addColorStop(1, r.mine ? 'rgba(32,23,5,0.62)' : 'rgba(4,7,10,0.48)');
  g.fillStyle = wash;
  g.fillRect(x0 - 14, y + 3, total + 16, ROW - 7);

  let x = x0 + pad;
  label(g, attacker, x, cy, { ...nameOpt, color: aCol });
  x += aw + 10;

  const fn = WEAPON_ICON[r.weapon] || ICON.rifle;
  icon(fn, g, x, cy - 9, iconW, 18, COL.white);
  x += iconW;

  if (r.headshot) {
    icon(ICON.skull, g, x + 1, cy - 8, 16, 16, COL.amber);
    x += skullW;
  }
  x += 10;
  label(g, victim, x, cy, { ...nameOpt, color: vCol });

  g.restore();
}
